import {
  Group,
  Flex,
  Badge,
  useMantineColorScheme,
  Divider,
} from "@mantine/core"
import { ChatAvatar } from "./ChatAvatar"
import { ChatName } from "./ChatName"
import { useDisclosure } from "@mantine/hooks"
import { DefaultError } from "../../../-components/error-context"
import { UpdateChatModal } from "./UpdateChatModal"
import { useGetAuthenticatedUser } from "@/routes/app/-components/use-authenticated-user"
import { DottedMenu } from "@components/dotted-menu"
import { openConfirmModal } from "@mantine/modals"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import {
  getGetChatByIdQueryKey,
  getGetChatsQueryKey,
  getUserHasArchivedChatQueryKey,
  useDeleteChat,
  useGetChatByIdSuspense,
  useToggleInactivateChat,
  useToggleMuteChat,
  useLeaveChat,
} from "@/orval/gen"
import { NavigateBackButton } from "@components/NavigateBackButton"
import { useBreakpoints } from "@/utils/use-breakpoints"

interface Props {
  chatId: number
  rightSection?: React.ReactElement
}

export function ChatHeaderGroup({ chatId, rightSection }: Props) {
  const { data: chat } = useGetChatByIdSuspense({
    chatId,
  })
  const { colorScheme } = useMantineColorScheme()
  const { xs, sm } = useBreakpoints()

  if (!chat) throw new DefaultError({ title: "Something went wrong." })

  const [
    updateModalOpened,
    { open: openUpdateModal, close: closeUpdateModal },
  ] = useDisclosure(false)

  const currentUserId = useGetAuthenticatedUser().data.userId
  const currentUserMember = chat.chatMembers.find(
    (cm) => cm.userId === currentUserId
  )
  const userIsAdmin = currentUserMember?.isAdmin

  const canEdit = chat.chatType !== "Direct" && userIsAdmin
  const queryClient = useQueryClient()
  const { mutateAsync: muteChat } = useToggleMuteChat({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetChatsQueryKey(),
        })
        queryClient.invalidateQueries({
          queryKey: getGetChatByIdQueryKey({ chatId: chat.chatId }),
        })
      },
    },
  })
  const navigate = useNavigate()
  const { mutateAsync: inactivateChat } = useToggleInactivateChat({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetChatsQueryKey(),
        })
        queryClient.invalidateQueries({
          queryKey: getGetChatByIdQueryKey({ chatId: chat.chatId }),
        })
        queryClient.invalidateQueries({
          queryKey: getUserHasArchivedChatQueryKey(),
        })
        navigate({ to: "/app/com/chat" })
      },
    },
  })
  const { mutateAsync: deleteChat } = useDeleteChat({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetChatsQueryKey(),
        })
        queryClient.invalidateQueries({
          queryKey: getUserHasArchivedChatQueryKey(),
        })
        if (["Feed", "BroadcastFeed"].includes(chat.chatType)) {
          navigate({ to: "/app/com/feeds" })
          return
        }
        navigate({ to: "/app/com/chat" })
      },
    },
  })
  const { mutateAsync: leaveChat } = useLeaveChat({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetChatsQueryKey(),
        })
        queryClient.invalidateQueries({
          queryKey: getGetChatByIdQueryKey({ chatId: chat.chatId }),
        })
        queryClient.invalidateQueries({
          queryKey: getUserHasArchivedChatQueryKey(),
        })
        navigate({ to: "/app/com/chat" })
      },
    },
  })
  const { t } = useTranslation("app_server")

  const menuItems = [
    ...((["Direct", "PrivateGroup", "PublicGroup"].includes(chat.chatType) && [
      {
        children: currentUserMember?.isMuted ? t("Unmute") : t("Mute"),
        onClick: async () =>
          await muteChat({ params: { chatId: chat.chatId } }),
      },
      {
        children: currentUserMember?.isInactive ? t("Unarchive") : t("Archive"),
        onClick: async () =>
          await inactivateChat({ params: { chatId: chat.chatId } }),
      },
      {
        children: chat.chatType === "Direct" ? t("Delete") : t("Leave"),
        onClick: () =>
          openConfirmModal({
            title: t("AreYouSure"),
            centered: true,
            cancelProps: { children: t("Cancel") },
            confirmProps: {
              children:
                chat.chatType === "Direct" ? t("YesDelete") : t("YesLeave"),
              color: "red",
            },
            onConfirm: async () => {
              navigate({ to: "/app/com/chat" })
              await leaveChat({ params: { chatId: chat.chatId } })
            },
          }),
      },
    ]) ||
      []),
    ...(userIsAdmin && chat.isEmpty
      ? [
          {
            children: t("Delete"),
            onClick: () =>
              openConfirmModal({
                title: t("AreYouSure"),
                centered: true,
                cancelProps: { children: t("Cancel") },
                confirmProps: { children: t("YesDelete"), color: "red" },
                onConfirm: async () =>
                  await deleteChat({ params: { chatId: chat.chatId } }),
              }),
          },
        ]
      : []),
  ]

  return (
    <React.Fragment>
      <Group
        wrap="nowrap"
        justify="space-between"
        w={"100%"}
        style={{
          borderBottom: `1px solid ${colorScheme === "dark" ? "var(--mantine-color-dark-4)" : "var(--mantine-color-gray-3)"}`,
        }}
        pb={4}
      >
        {xs || sm ? (
          <>
            <NavigateBackButton />
            <Divider orientation="vertical" />
          </>
        ) : null}
        <Group
          miw={0}
          w={"100%"}
          wrap="nowrap"
          {...(chat.chatType !== "Direct"
            ? {
                style: {
                  cursor: "pointer",
                },
                onClick: openUpdateModal,
              }
            : undefined)}
        >
          <ChatAvatar chat={chat} />
          {chat.isHidden ? <Badge>{t("Hidden")}</Badge> : null}
          <ChatName chat={chat} />
          {rightSection ? (
            <>
              <Flex flex="1" />
              {rightSection}
            </>
          ) : null}
        </Group>
        {menuItems.length > 0 && <DottedMenu items={menuItems} />}
      </Group>
      <UpdateChatModal
        key={chat.chatId}
        opened={updateModalOpened}
        onClose={closeUpdateModal}
        chat={chat}
        readonly={!canEdit}
      />
    </React.Fragment>
  )
}
